import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { attendanceApi } from '@/services/api/endpoints/attendance';
import { periodsApi, sectionsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AttendanceStatus, AttendanceType, RegisterRow } from '@/types/domain';

const STATUSES: { value: AttendanceStatus; label: string; tone: string }[] = [
  { value: 'PRESENT', label: 'Present', tone: 'bg-success/10 text-success border-success/30' },
  { value: 'ABSENT', label: 'Absent', tone: 'bg-danger/10 text-danger border-danger/30' },
  { value: 'LATE', label: 'Late', tone: 'bg-warning/10 text-warning border-warning/30' },
  { value: 'EXCUSED', label: 'Excused', tone: 'bg-info/10 text-info border-info/30' },
  { value: 'LEAVE', label: 'Leave', tone: 'bg-surface-2 text-muted border-line' },
];

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Taking a register.
 *
 * Marked as a whole class in one submission, because that is how a register is
 * actually taken — thirty separate requests would make a half-marked register a
 * routine outcome.
 *
 * Children who have not been marked show no status rather than defaulting to
 * present. A default would silently record thirty presents for a teacher who
 * opened the screen and walked away.
 */
export function AttendancePage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  // ?sectionId=&date= open a particular register — the teacher home links here.
  const [searchParams] = useSearchParams();
  const [sectionId, setSectionId] = useState(searchParams.get('sectionId') ?? '');
  const [date, setDate] = useState(searchParams.get('date') ?? today());
  const [type, setType] = useState<AttendanceType>('DAILY');
  const [periodId, setPeriodId] = useState('');
  const [marks, setMarks] = useState<Record<string, AttendanceStatus>>({});

  const sections = useQuery({
    queryKey: queryKeys.sections.list({ size: 200 }),
    queryFn: () => sectionsApi.list({ page: 0, size: 200 }),
  });

  const selectedSection = sections.data?.data.find((section) => section.id === sectionId);

  const periods = useQuery({
    queryKey: queryKeys.periods.forCampus(selectedSection?.campusId ?? ''),
    queryFn: () => periodsApi.list(selectedSection?.campusId ?? ''),
    enabled: type === 'PERIOD' && selectedSection?.campusId !== undefined,
  });

  const registerParams = {
    sectionId,
    date,
    type,
    ...(type === 'PERIOD' && periodId !== '' ? { periodId } : {}),
  };

  const register = useQuery({
    queryKey: queryKeys.attendance.register(registerParams),
    queryFn: () => attendanceApi.register(registerParams),
    enabled: sectionId !== '' && (type === 'DAILY' || periodId !== ''),
  });

  /* Seeded from what is already recorded, so reopening a marked register shows
     the existing marks rather than a blank sheet the teacher would redo. */
  useEffect(() => {
    if (register.data === undefined) {
      return;
    }
    const existing: Record<string, AttendanceStatus> = {};
    for (const row of register.data.rows) {
      if (row.status !== undefined) {
        existing[row.enrollmentId] = row.status;
      }
    }
    setMarks(existing);
  }, [register.data]);

  const save = useMutation({
    mutationFn: () =>
      attendanceApi.mark({
        sectionId,
        attendanceDate: date,
        attendanceType: type,
        periodId: type === 'PERIOD' ? periodId : undefined,
        marks: Object.entries(marks).map(([enrollmentId, status]) => ({ enrollmentId, status })),
      }),
    onSuccess: (updated) => {
      toast.success(`Register saved — ${updated.markedCount} of ${updated.totalCount} marked`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all });
    },
    onError: (error) => handleApiError(error),
  });

  const markAll = (status: AttendanceStatus) => {
    const everyone: Record<string, AttendanceStatus> = {};
    for (const row of register.data?.rows ?? []) {
      everyone[row.enrollmentId] = status;
    }
    setMarks(everyone);
  };

  const unmarkedCount = (register.data?.rows.length ?? 0) - Object.keys(marks).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Attendance"
        description="Mark a class register. Marking again corrects the entries rather than duplicating them."
      />

      <Card className="p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            label="Class"
            value={sectionId}
            onChange={(event) => {
              setSectionId(event.target.value);
              setMarks({});
            }}
          >
            <option value="">Select a class…</option>
            {sections.data?.data.map((section) => (
              <option key={section.id} value={section.id}>
                {section.gradeName !== undefined ? `${section.gradeName} · ` : ''}
                {section.name}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Date"
            type="date"
            value={date}
            max={today()}
            onChange={(event) => {
              setDate(event.target.value);
              setMarks({});
            }}
          />

          <SelectField
            label="Register"
            value={type}
            onChange={(event) => {
              setType(event.target.value as AttendanceType);
              setPeriodId('');
              setMarks({});
            }}
          >
            <option value="DAILY">Daily</option>
            <option value="PERIOD">Single period</option>
          </SelectField>

          {type === 'PERIOD' && (
            <SelectField
              label="Period"
              required
              value={periodId}
              onChange={(event) => {
                setPeriodId(event.target.value);
                setMarks({});
              }}
            >
              <option value="">Select a period…</option>
              {periods.data
                ?.filter((period) => period.teachable)
                .map((period) => (
                  <option key={period.id} value={period.id}>
                    {period.name}
                  </option>
                ))}
            </SelectField>
          )}
        </div>
      </Card>

      {sectionId === '' ? (
        <Card className="p-5">
          <p className="py-6 text-center text-sm text-subtle">
            Choose a class and a date to take the register.
          </p>
        </Card>
      ) : register.isPending ? (
        <LoadingState label="Loading register" />
      ) : register.error !== null ? (
        <ErrorState error={register.error} onRetry={() => void register.refetch()} />
      ) : register.data === undefined ? null : (
        <Card className="p-5">
          {register.data.holiday && (
            <p className="mb-4 rounded-lg bg-info-soft px-3 py-2 text-sm text-info">
              The school is closed on this date. Marks are not normally expected.
            </p>
          )}

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted">
                {register.data.rows.length} students
                {unmarkedCount > 0 && (
                  <span className="ml-2 text-warning">{unmarkedCount} still unmarked</span>
                )}
              </p>
            </div>

            {can(PERMISSIONS.STUDENT_ATTENDANCE_CREATE) && (
              <div className="flex flex-wrap items-center gap-2">
                {/* The common case is "everyone is here, except…", so filling the
                    sheet in one action and correcting the exceptions is faster
                    than thirty individual clicks. */}
                <Button size="sm" variant="ghost" onClick={() => markAll('PRESENT')}>
                  Mark all present
                </Button>
                <Button
                  loading={save.isPending}
                  disabled={Object.keys(marks).length === 0}
                  onClick={() => save.mutate()}
                >
                  Save register
                </Button>
              </div>
            )}
          </div>

          <ul className="divide-y divide-line">
            {register.data.rows.map((row) => (
              <RegisterRowItem
                key={row.enrollmentId}
                row={row}
                value={marks[row.enrollmentId]}
                readOnly={!can(PERMISSIONS.STUDENT_ATTENDANCE_CREATE)}
                onChange={(status) => setMarks((current) => ({ ...current, [row.enrollmentId]: status }))}
              />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function RegisterRowItem({
  row,
  value,
  readOnly,
  onChange,
}: {
  row: RegisterRow;
  value: AttendanceStatus | undefined;
  readOnly: boolean;
  onChange: (status: AttendanceStatus) => void;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-2.5">
      <div className="flex min-w-0 items-center gap-3">
        <span className="w-10 shrink-0 text-center font-mono text-xs text-subtle">
          {row.rollNumber ?? '—'}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm text-ink">{row.studentName}</p>
          <p className="font-mono text-xs text-subtle">{row.studentNumber}</p>
        </div>
      </div>

      {readOnly ? (
        value === undefined ? (
          <Badge tone="neutral">Not marked</Badge>
        ) : (
          <Badge tone="neutral">{value.toLowerCase()}</Badge>
        )
      ) : (
        <div className="flex flex-wrap gap-1" role="group" aria-label={`Attendance for ${row.studentName}`}>
          {STATUSES.map((status) => (
            <button
              key={status.value}
              type="button"
              aria-pressed={value === status.value}
              onClick={() => onChange(status.value)}
              className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                value === status.value
                  ? status.tone
                  : 'border-line text-subtle hover:border-line-strong hover:text-muted'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
